import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { showResult } from 'infrastructure/hooks/utils';
import dataService from 'services/dataService';
import { apiHistoricalActivities } from 'services/dataService/resources';

import { HistoricalActivityTypes } from '../constants';
import { getTodayRangeLocalTimestamps } from '../utils/time-range';

import type {
  IHistoryActivityProgram,
  IHistoryActionKey,
} from 'components/molecules/oneplaform/today-history-activities/types';

const HISTORICAL_ACTIVITIES_QUERY_KEY = 'home-dashboard/historical-activities';

type KnownProgramVariant = Exclude<
  NonNullable<IHistoryActivityProgram['variant']>,
  'default'
>;

const FALLBACK_PROGRAM_LABEL: Record<KnownProgramVariant, string> = {
  rpm: 'RPM',
  ccm: 'CCM',
};

interface IHistoricalActivityPatient {
  guid?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
}

interface IHistoricalActivityActor {
  guid?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
}

interface IHistoricalActivityMetadata {
  program?: string;
  programLabel?: string;
  programName?: string;
  isCCM?: boolean;
  noteMode?: string;
  [key: string]: unknown;
}

interface IHistoricalActivityApiItem {
  id?: string;
  type?: string;
  text?: string;
  timestamp?: string;
  timeLabel?: string;
  patient?: IHistoricalActivityPatient;
  actor?: IHistoricalActivityActor;
  metadata?: IHistoricalActivityMetadata;
}

interface IHistoricalActivitiesResponse {
  count?: number;
  page?: number;
  items?: number;
  timezone?: string;
  data?: IHistoricalActivityApiItem[];
}

interface IFetchHistoricalActivitiesParams {
  page: number;
  items: number;
}

export interface IUseHistoricalActivitiesOptions
  extends IFetchHistoricalActivitiesParams {
  profileGuid?: string;
}

const normalizeGuid = (guid?: string): string | undefined => {
  const trimmed = guid?.trim();
  return trimmed?.length ? trimmed.toLowerCase() : undefined;
};

const formatFullName = (
  participant?: IHistoricalActivityPatient | IHistoricalActivityActor,
): string => {
  if (!participant) return '';

  if (participant.fullName?.trim()) return participant.fullName.trim();

  const parts = [participant.firstName, participant.lastName]
    .map((part) => part?.trim())
    .filter(Boolean);

  return parts.join(' ');
};

const formatPatientName = (patient?: IHistoricalActivityPatient): string =>
  formatFullName(patient);

const formatActorName = (
  actor: IHistoricalActivityActor | undefined,
  profileGuid?: string,
): string => {
  const actorGuid = normalizeGuid(actor?.guid);
  const normalizedProfileGuid = normalizeGuid(profileGuid);

  if (
    actorGuid &&
    normalizedProfileGuid &&
    actorGuid === normalizedProfileGuid
  ) {
    return 'You';
  }

  return formatFullName(actor);
};

const getProgramLabelFromMetadata = (
  metadata?: IHistoricalActivityMetadata,
): string | undefined => {
  if (!metadata) return undefined;

  const labelCandidate =
    metadata.programLabel ?? metadata.programName ?? metadata.program;

  if (typeof labelCandidate !== 'string') return undefined;

  const trimmed = labelCandidate.trim();

  return trimmed.length ? trimmed : undefined;
};

const getProgramVariantFromLabel = (
  label?: string,
): KnownProgramVariant | undefined => {
  if (!label) return undefined;

  const normalized = label.trim().toLowerCase();

  if (normalized === 'rpm') return 'rpm';
  if (normalized === 'ccm') return 'ccm';

  return undefined;
};

const getProgramVariantFromMetadata = (
  metadata?: IHistoricalActivityMetadata,
): KnownProgramVariant | undefined => {
  if (metadata?.isCCM === true) return 'ccm';
  if (metadata?.isCCM === false) return 'rpm';

  return undefined;
};

const buildActionLabel = (
  activityType?: string,
  metadata?: IHistoricalActivityMetadata,
): string => {
  switch (activityType) {
    case HistoricalActivityTypes.ReadingReview:
      return 'reviews readings';
    case HistoricalActivityTypes.VoiceCall:
      return 'called';
    case HistoricalActivityTypes.Sms:
      return 'sent SMS to';
    case HistoricalActivityTypes.ClinicalNote:
      if (metadata?.noteMode === 'manual') {
        return 'manually logged clinical note for';
      }
      return 'logged clinical note for';
    case HistoricalActivityTypes.KeyNote:
      return 'created key note for';
    case HistoricalActivityTypes.Medication:
      return 'added medication for';
    case HistoricalActivityTypes.Condition:
      return 'added condition for';
    case HistoricalActivityTypes.AppointmentBooked:
      return 'booked appointment for';
    case HistoricalActivityTypes.AppointmentRescheduled:
      return 'rescheduled appointment for';
    default:
      return 'updated';
  }
};

const buildActivityDescription = (
  actorName: string,
  actionLabel: string,
  patientName: string,
): string => `${actorName} ${actionLabel} ${patientName}`.trim();

const buildProgramTag = (
  metadata?: IHistoricalActivityMetadata,
): IHistoryActivityProgram | undefined => {
  const metadataLabel = getProgramLabelFromMetadata(metadata);

  if (metadataLabel) {
    const variant = getProgramVariantFromLabel(metadataLabel);
    const normalizedLabel = variant
      ? metadataLabel.trim().toUpperCase()
      : metadataLabel.trim();

    return {
      label: normalizedLabel,
      ...(variant ? { variant } : {}),
    };
  }

  const fallbackVariant = getProgramVariantFromMetadata(metadata);

  if (!fallbackVariant) return undefined;

  return {
    label: FALLBACK_PROGRAM_LABEL[fallbackVariant],
    variant: fallbackVariant,
  };
};

const formatTimestampLabel = (timestamp?: string): string => {
  if (!timestamp) return '';

  const parsedTimestamp = dayjs(timestamp);

  return parsedTimestamp.isValid() ? parsedTimestamp.format('HH:mm A') : '';
};

const normalizeActionKey = (
  activityType?: string,
): IHistoryActionKey | undefined => {
  if (!activityType) return undefined;

  const validKeys: IHistoryActionKey[] = [
    'readingReview',
    'voiceCall',
    'sms',
    'clinicalNote',
    'clinicalNoteManual',
    'keyNote',
    'medication',
    'condition',
    'appointmentBooked',
    'appointmentRescheduled',
    'updated',
  ];

  return validKeys.includes(activityType as IHistoryActionKey)
    ? (activityType as IHistoryActionKey)
    : undefined;
};

const formatHistoricalActivities = (
  items: IHistoricalActivityApiItem[] | undefined,
  profileGuid?: string,
) => {
  if (!items?.length) return [];

  const mapped = items
    .map((item) => {
      if (!item?.id) {
        return null;
      }

      const normalizedPatientGuid = item.patient?.guid?.trim();
      const actionLabel = buildActionLabel(item.type, item.metadata);
      const actorName = formatActorName(item.actor, profileGuid);
      const patientName = formatPatientName(item.patient);
      const description = buildActivityDescription(
        actorName,
        actionLabel,
        patientName,
      );

      return {
        id: item.id,
        description,
        actorName,
        patientName,
        timestampLabel: formatTimestampLabel(item.timestamp),
        patientGuid: normalizedPatientGuid?.length
          ? normalizedPatientGuid
          : undefined,
        actionLabel,
        actionKey: normalizeActionKey(item.type),
        program: buildProgramTag(item.metadata),
      };
    })
    .filter(Boolean);

  return mapped;
};

const fetchHistoricalActivities = async (
  params: IFetchHistoricalActivitiesParams,
): Promise<IHistoricalActivitiesResponse> => {
  const { from, to } = getTodayRangeLocalTimestamps();
  const endpoint = apiHistoricalActivities({
    page: Math.max(params.page, 1),
    items: Math.max(params.items, 1),
    activitiesDateFrom: from,
    activitiesDateTo: to,
  });

  const { data, error } =
    await dataService.getList<IHistoricalActivitiesResponse>(endpoint);

  if (error) {
    throw new Error(error);
  }

  return (
    data ?? {
      count: 0,
      page: params.page,
      items: params.items,
      data: [],
    }
  );
};

export const useHistoricalActivities = (
  options: IUseHistoricalActivitiesOptions,
) => {
  const normalizedPage = Math.max(options.page, 1);
  const normalizedItems = Math.max(options.items, 1);
  const normalizedProfileGuid = normalizeGuid(options.profileGuid);

  const queryResult = useQuery<IHistoricalActivitiesResponse, Error>({
    queryKey: [
      HISTORICAL_ACTIVITIES_QUERY_KEY,
      normalizedPage,
      normalizedItems,
    ],
    queryFn: () =>
      fetchHistoricalActivities({
        page: normalizedPage,
        items: normalizedItems,
      }),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (queryResult.error) {
      showResult(queryResult.error.message);
    }
  }, [queryResult.error]);

  const entries = useMemo(
    () =>
      formatHistoricalActivities(
        queryResult.data?.data ?? [],
        normalizedProfileGuid,
      ),
    [queryResult.data?.data, normalizedProfileGuid],
  );

  return {
    entries,
    total: queryResult.data?.count ?? 0,
    page: normalizedPage,
    pageSize: normalizedItems,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    refetch: queryResult.refetch,
  };
};
